import { observer } from "mobx-react-lite/dist"
import { useTheme } from "theme/useTheme"
import Paper from "@mui/material/Paper"
import Typography from "@mui/material/Typography"
import Stack from "@mui/material/Stack"
import { ServiceRateTypeEnum } from "@gap/backend/app-services/services/models/ServiceRateTypesDto"
import ProposalsEditPaymentBlockCheck from "./payment-blocks/ProposalsEditPaymentBlockCheck"
import { PaymentBlockType, canSeeBasedOnBlockType, checkFieldNameForBlockType } from "features/proposals/proposalsUtils"
import { notNull } from "@gap/utils/utils"
import { useMemo } from "react"
import ProposalsEditPaymentBlockInput from "./payment-blocks/ProposalsEditPaymentBlockInput"
import InputAdornment from "@mui/material/InputAdornment"
import TextField from "@mui/material/TextField"
import { useProposalsEditPaymentStore } from "./ProposalsEditPaymentStore"

export interface PaymentBlockWrapperComponentProps {
   rateType: ServiceRateTypeEnum
}

interface PaymentBlockProps {
   rateType: ServiceRateTypeEnum
   blockType: PaymentBlockType
}

export default observer(function ProposalsEditPaymentPaymentBlock({ rateType, blockType }: PaymentBlockProps) {
   const theme = useTheme()

   const component = useMemo(() => {
      switch (blockType) {
         case 'Annual payment':
            return <ProposalsEditPaymentBlockInput fieldName="annualPaymentDiscount" rateType={rateType} fieldType="optional" />
         case 'Staged payments':
            return <Stack sx={{ flexDirection: 'row', gap: .5 }}>
               <ProposalsEditPaymentBlockInput fieldName="stagedPaymentUpfront" rateType={rateType} formNumberConfig={{ maxDecimalPlaces: 0, maxIntegerLength: 2 }} fieldType="required" />
               <ProposalsEditPaymentBlockInput fieldName="stagedPaymentMilestone" rateType={rateType} formNumberConfig={{ maxDecimalPlaces: 0, maxIntegerLength: 2 }} fieldType="optional" />
               <StagedPaymentCompletion rateType={rateType} />
            </Stack>
         case 'Upfront payment in full':
            return <ProposalsEditPaymentBlockInput fieldName="upfrontPaymentInFullDiscount" rateType={rateType} fieldType="optional" />
      }
      return null
   }, [blockType, rateType,])

   if (!canSeeBasedOnBlockType[blockType].includes(rateType)) {
      return undefined
   }

   return <Paper sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: 3,
      border: `1px solid ${theme.palette.gap.primary['200']}`,
      borderRadius: '16px',
      padding: 2,
   }} elevation={0}>
      <Stack sx={{ flexDirection: 'row', alignItems: 'center' }}>
         <Typography variant="body1" sx={{ flexGrow: 1 }}>{blockType}</Typography>
         <ProposalsEditPaymentBlockCheck rateType={rateType} fieldName={notNull(checkFieldNameForBlockType[blockType])} />
      </Stack>

      {component && <Stack>
         {component}
      </Stack>}
   </Paper>
})

const StagedPaymentCompletion = observer(function StagedPaymentCompletion({ rateType }: { rateType: ServiceRateTypeEnum }) {
   const proposalEditPaymentStore = useProposalsEditPaymentStore()
   const stepData = notNull(proposalEditPaymentStore.getStepByRateType(rateType))

   let stagedPaymentCompletion = 0
   if ('stagedPaymentUpfront' in stepData) {
      stagedPaymentCompletion = 100 - stepData.stagedPaymentUpfront - stepData.stagedPaymentMilestone
   }

   return <TextField
      disabled={true}
      InputProps={{
         endAdornment: <InputAdornment position="end" sx={{ opacity: 0.5 }}>%</InputAdornment>,
      }}
      variant='outlined'
      label="Completion"
      sx={{ width: '134px', mr: .5, "& fieldset": { border: '1px dashed' }, }}
      value={stagedPaymentCompletion}
      InputLabelProps={{ shrink: true }}
   />
})
